import { allocate } from '@shared/money/allocate'
import type { Cents } from '@shared/money/cents'

/**
 * Os atalhos de divisão da sheet de breakdown — *"por percentual, em partes
 * iguais, ou 'o resto vai para X'"*
 * (`Docs/05-Plano/Fase 5 - Breakdown e comentários.md`).
 *
 * ## Tudo passa por `allocate`, e nada arredonda à mão
 *
 * `allocate` garante que a soma **fecha**: `sumCents(allocate(t, pesos)) === t`,
 * sempre, com o resto indo para o último item de propósito — *"não é o rateio
 * mais justo, é o mais auditável"*
 * (`Docs/03-Arquitetura/Dinheiro, datas e fuso.md`).
 *
 * Dividir R$ 100,00 em três e escrever `33,33` três vezes daria R$ 99,99, e o
 * centavo que sobra viraria um resto de um centavo que ninguém pediu — a
 * transação apareceria eternamente "quase detalhada". Com `allocate`, é
 * `[3333, 3333, 3334]`.
 *
 * ## O que se divide é o RESTO, não o total
 *
 * Quem já separou R$ 200 de uma compra de R$ 312,45 e pede "partes iguais"
 * quer dividir os R$ 112,45 que sobraram. Dividir o total produziria itens que
 * somados passam da transação — o erro que `breakdownRemainder` recusa, chegando
 * pelo atalho que devia facilitar.
 *
 * ## Tudo em módulo
 *
 * `BreakdownItem.amountCents` é sempre positivo; o sinal mora na transação. Uma
 * despesa e uma receita de mesmo módulo se dividem igual.
 */

/**
 * `remainderCents` em `parts` fatias que somam exatamente ele.
 *
 * `parts < 1` devolve lista vazia: dividir em zero partes não é uma divisão, e
 * `allocate` com pesos vazios devolveria `[]` de qualquer forma — dizer isso
 * aqui evita que a tela pergunte.
 */
export function splitEqually(remainderCents: Cents, parts: number): Cents[] {
  if (!Number.isInteger(parts) || parts < 1) return []
  return allocate(remainderCents, Array.from({ length: parts }, () => 1))
}

/**
 * `remainderCents` repartido pelos percentuais dados.
 *
 * **Os percentuais são PESOS, não uma promessa de somar 100.** Passar
 * `[50, 50]` e `[1, 1]` dá o mesmo resultado, e `[50, 30]` reparte tudo na
 * proporção 5:3 — porque o que se divide é o resto inteiro, e sobrar uma fatia
 * sem dono seria devolver ao resto o que a pessoa acabou de mandar tirar dele.
 *
 * Quem quiser detalhar só uma parte usa um item só, com o valor.
 */
export function splitByWeights(remainderCents: Cents, weights: number[]): Cents[] {
  if (weights.length === 0) return []
  return allocate(remainderCents, weights)
}
